import {formatDate} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {Component, AfterViewInit} from '@angular/core';
import {ActivatedRoute, NavigationExtras, Params, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ox/translate';
import {
  ActiveVehicleActions,
  BaseResponse,
  DecryptVinService,
  GarageService,
  selectActiveVehicle,
  selectGarage,
  selectProfile,
  TrackingService,
  AdobeLaunchService,
  selectFeatureFlag
} from '@ox/data';
import {RecallsGfasService, RecallsPageService} from '@ox/recalls';
import {
  AdobeDataLayerBaseCall,
  Brands,
  ExpandCollection,
  GfasPayload,
  Profile,
  RecallPayload,
  RecallsVehicle,
  UIFeatureFlag,
  Vehicle,
  WarrantyPayload
} from '@ox/typings';
import {AppConfigService, AutoDestroy, SessionStorageService} from '@ox/utils';
import {WarrantiesService} from '@webu/data';
import {map, mergeMap, Observable, of, Subject, take, takeUntil, takeWhile, tap} from 'rxjs';
import {recallsConfigItems} from '../recalls.constant';
import {OX_KEYS} from '@ox/config';

const AUTH = 'authenticated';
const UNAUTH = 'unauthenticated';

type unauth = typeof UNAUTH;
type auth = typeof AUTH
type UserState = auth | unauth;

@Component({
  selector: 'webu-recall-page',
  templateUrl: './recalls.component.html'
})

export class RecallsComponent {
  hideWarranties: boolean = this.appConfig.appSettings.moduleConfig.hideWarranties ? this.appConfig.appSettings.moduleConfig.hideWarranties : false;
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public _recallsVehicle: RecallsVehicle = {
    year: '',
    make: '',
    model: '',
    renderImageUrl: '',
    vin: '',
    odometer: '',
    odometerLastUpdated: -1
  };
  public onStarLegalUpdtFailMsg = false; // <- update from onstar legal component???
  public onStarLegalDisplayMsg = false; // <- update from onstar legal component???
  public mmyFound = false;
  public isOnstarActive = false;
  public landingFaq: ExpandCollection[] = recallsConfigItems.recallCenter.landingFaq;
  public foundResultsFaq: ExpandCollection[] = recallsConfigItems.recallCenter.foundResultsFaq;
  public noResultsFoundFaq: ExpandCollection[] = recallsConfigItems.recallCenter.noResultsFoundFaq;
  public userState: UserState = UNAUTH;
  public profile: Profile | undefined;
  public recallCenterWhatNowSecondParagraph: string;
  public loading = false;
  public recallResults: RecallPayload[] = [];
  public completeRecallResults: RecallPayload[] = [];
  public specialWarranties: RecallPayload[] = [];
  public flag = {
    recallSearchCompleted: false,
    nhtsaRecallExists: false,
    nonNhtsaRecallExists: false,
    invalidVinError: false,
    recallErrorState: false,
    warrantyErrorState: false,
    recallWidgetSignupSuccess: false,
    recallsNotificationSignupError: false,
    isAnotherSearch: false,
    displayStaticWidget: false,
    warrantySearchCompleted: false
  };
  public vinAttributes: { pattern: RegExp; maxlength: number } = this.appConfig.fieldAttributes.vehicle.vin;
  public odometerAttributes: { pattern: RegExp; maxlength: number } = this.appConfig.fieldAttributes.vehicle.odometer;
  public currentTakataRecalls: string;
  public vehicles: Vehicle[] = [];
  public warrantyResults: { expired: WarrantyPayload[]; active: WarrantyPayload[]; };
  public brand: Brands = this.appConfig.bml?.brand;
  private omni = window.s || null;
  public isTakataSignupSuccess$ = this.recallsPage.takataSignupMessage$;

  private profile$ = this.store.select(selectProfile).pipe(
    takeUntil(this.destroy$),
    tap((profile) => {
      if (profile) {
        this.profile = profile;
        this.userState = AUTH;
      } else {
        this.profile = undefined;
        this.userState = UNAUTH;
      }
    })).subscribe();

  private dataLayerV2Enabled$ = this.store.select(selectFeatureFlag({id: UIFeatureFlag.DATALAYER_V2, group: 'UI'}))
    .pipe(
      takeWhile((val) => !val, true)
    );

  private vin: string;
  private serverError = false;
  private invalidVin: boolean;
  private errorMessage: string;
  private urlVin = '';
  private vin_enc: string;
  private decryptingVin: boolean;
  private stayUpToDateRecalls: string[];
  private garage: Map<string, Vehicle>;
  public prop51:string | undefined;
  public prop53: string;

  private garage$ = this.store.select(selectGarage).pipe(
    takeUntil(this.destroy$),
    tap((garage) => {
      if (garage.size > 0) {
        this.garage = garage; // map by id
        this.vehicles = [...Array.from(this.garage.values())];
      }
    })
  ).subscribe();
  private getSearchedVin$ = this.recallsGfas.searchedVinEmitter.pipe(
    takeUntil(this.destroy$),
    map((vin: string) => {
      if (!vin) return;
      this.setActiveVehicle(vin);
      this.setVinInUrl(vin);
    })
  ).subscribe();
  private takateEligibleRecallNumbers: string[];
  private routeParams$ = this.route.queryParams.pipe(
    takeUntil(this.destroy$),
    tap((params: Params) => {
      this.urlVin = params['vin'];
      this.vin_enc = params['vin_enc'];
      if (this.vin_enc) {
        this.decryptingVin = true;
        this.decryptVin(this.vin_enc);
      } else {
        this.receivedVinUpdate(this.urlVin);
      }
    })
  ).subscribe();
  public activeVehicle$ = this.store.select(selectActiveVehicle)
    .pipe(
      takeUntil(this.destroy$),
      tap((vehicle) => {
        if (vehicle) {
          this.activeVehicle = vehicle;
          this.isOnstarActive = vehicle.onStarActive || false;
          this.mmyFound = !vehicle.vin;
          if (!this.vin_enc && !this.decryptingVin && !this.urlVin) this.setVinInUrl(vehicle.vin);
          if (!vehicle.vin) {
            this.isYMMVehicle();
          }
        }
      })
    ).subscribe();
  private trackingTitleLoad = this.route.title?.pipe(
    takeUntil(this.destroy$),
    tap((title: string | undefined) => {
      if (title) {
        this.adobeLaunch.updatePageTitle(title);
      }
    })
  ).subscribe();

  private odometerUpdate$ = this.recallsPage.odometerUpdate$.subscribe({
    next: (miles) => {
      if (miles) this.vehicleMileageUpdate(miles);
    }
  });


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private appConfig: AppConfigService,
    private sessionStorage: SessionStorageService,
    private recallsGfas: RecallsGfasService,
    private translate: TranslateService,
    private trackingService: TrackingService,
    private decryptVinService: DecryptVinService,
    private garageService: GarageService,
    private adobeLaunch: AdobeLaunchService,
    private warranty: WarrantiesService,
    private recallsPage: RecallsPageService
  ) {
  }

  private _activeVehicle: Vehicle;

  get activeVehicle() {
    return this._activeVehicle;
  }

  set activeVehicle(vehicle) {
    this._activeVehicle = vehicle;
  }

  get vehicleCount() {
    return this.garage?.size || 0;
  }

  get getCurrentDate(): string {
    return (this.appConfig.locale) ? formatDate(Date.now(), this.appConfig.appSettings.moduleConfig.dateFormat.mediumDate, this.appConfig.locale) : '';
  }

  public takataSignupMessage(isSuccessful: boolean) {
    this.flag.recallWidgetSignupSuccess = isSuccessful;
    this.flag.recallsNotificationSignupError = !isSuccessful;
    this.scrollTop();
  }

  public updateToActive(id: string) {
    const selectedVehicle = this.garage.get(id);
    if (selectedVehicle && this.urlVin != selectedVehicle?.vin) {
      this.setVinInUrl(selectedVehicle.vin);
    }
  }

  public vehicleMileageUpdate(inputMileage: string) {
    if (this._recallsVehicle.vin === this.activeVehicle?.vin && this.profile) {
      this.garageService.updateVehicleMileage(
        this.profile.profileId,
        {...this.activeVehicle, odometer: inputMileage, odometerLastUpdated: Date.now()}).subscribe({
        next: () => {
          this.recallsPage.odometerUpdateError = false;
          this._recallsVehicle.odometerLastUpdated = Date.now();
          this._recallsVehicle.odometer = inputMileage;
          this.recallsPage.recallsVehicle = {...this._recallsVehicle};
        },
        error: (e) => {
          this.recallsPage.odometerUpdateError = true;
          console.warn(`Error updating mileage: ${e}`);
        }
      });
    } else {
      this._recallsVehicle.odometerLastUpdated = Date.now();
      this._recallsVehicle.odometer = inputMileage;
      this.recallsPage.recallsVehicle = {...this._recallsVehicle};
    }
    this.warrantyResults = this.updateWarranties(this.warrantyResults, inputMileage);
  }

  public updateWarranties(data: { expired: WarrantyPayload[], active: WarrantyPayload[] }, vehicleOdemeter: string) {
    const newWarrantiesObj: { expired: WarrantyPayload[], active: WarrantyPayload[] } = {expired: [], active: []};
    for (const [key, value] of Object.entries(data)) {
      value.forEach((current) => {
        const isActive = current?.[OX_KEYS.TAB_DETAIL_ITEM.STATUS] == OX_KEYS.TAB_DETAIL_ITEM.ACTIVE;
        const isExpired = current?.[OX_KEYS.TAB_DETAIL_ITEM.STATUS] == OX_KEYS.TAB_DETAIL_ITEM.EXPIRED;
        const endDate = new Date(current?.[OX_KEYS.TAB_DETAIL_ITEM.END_DATE]);
        const currentDate = new Date();
        const isInDateRange = currentDate < endDate;
        const isInMileageRange = Number(vehicleOdemeter) < Number(current?.[OX_KEYS.TAB_DETAIL_ITEM.END_MILEAGE]);

        if (isActive && isInDateRange && isInMileageRange) {
          newWarrantiesObj.active.push(current);
        } else if (isExpired) {
          newWarrantiesObj.expired.push(current);
        } else if (isInDateRange && isInMileageRange) {
          newWarrantiesObj.active.push(current);
        } else {
          newWarrantiesObj.expired.push(current);
        }
      });
    }
    return newWarrantiesObj;
  }

  private setVinInUrl(vin: string) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        vin
      }
    });
  }

  private receivedVinUpdate(receivedVin: string) {
    if (this.isVinValid(receivedVin)) {
      this.vin = receivedVin.toUpperCase();
      this.getRecalls();
      if (!this.hideWarranties) this.getWarranties();
    }
  }

  private getWarranties() {
    this.loading = true;
    this._searchWarranties()
      .subscribe({
        next: (data: { expired: WarrantyPayload[], active: WarrantyPayload[] }) => this.processWarranties(data),
        error: (error) => this.onWarrantyError(error)
      });
  }

  private _searchWarranties(): Observable<{ expired: WarrantyPayload[], active: WarrantyPayload[] }> {
    this.setSearchVehicle({});

    return this.warranty.searchForWarranties(this.vin, this._recallsVehicle.odometer).pipe(
      map((resp: { expired: WarrantyPayload[], active: WarrantyPayload[] }) => {
        if (!resp) {
          this.setActiveVehicle(this.vin);
          this.onWarrantiesResponseError(resp);
        }
        return resp;
      }));
  }

  private processWarranties(data: { expired: WarrantyPayload[], active: WarrantyPayload[] }) {
    this.warrantyResults = data;
    this.combineSpecialWarranties();
    this.flag.warrantySearchCompleted = true;
    this.flag.warrantyErrorState = false;
    this.recallsPage.warrantError = false;
    this.loading = false;
  }

  private onWarrantyError(error: HttpErrorResponse) {
    this.flag.warrantyErrorState = this.serverError = true;
    this.recallsPage.warrantError = true;
    this.flag.warrantySearchCompleted = true;
    this.errorMessage = error.message;
    this.trackingService.trackEvent('event25', 'warranty.retrieval.failed');
    this.setWarrantyErrorProperties();
  }

  private combineSpecialWarranties() {
    //not the most efficient way of doing this, but using it for the moment just to get things working
    if (this.specialWarranties.length > 0) {
      for (const warranty of this.specialWarranties) {
        if (this.warrantyResults.active.length > 0) {
          for (const activeWarranty of this.warrantyResults.active) {
            if (activeWarranty.description.includes(warranty.gfaNumber) ||
              activeWarranty.description.includes(warranty.currentBusinessUnitInfo.alternateGfaNbr) ||
              activeWarranty.description.includes(warranty.releaseBusinessUnitInfo.alternateGfaNbr)) {
              activeWarranty.specialCoverageData = warranty.gfaTexts;

            }
          }
        }
        if (this.warrantyResults.active.length > 0) {
          for (const expiredWarranty of this.warrantyResults.expired) {
            if (expiredWarranty.description.includes(warranty.gfaNumber) ||
              expiredWarranty.description.includes(warranty.currentBusinessUnitInfo.alternateGfaNbr) ||
              expiredWarranty.description.includes(warranty.releaseBusinessUnitInfo.alternateGfaNbr)) {
              expiredWarranty.specialCoverageData = warranty.gfaTexts;
            }
          }
        }
      }
    }
  }

  private _search() {
    this.mmyFound = false;
    this.setSearchVehicle({});
    return this.recallsGfas.searchForRecalls(this.vin).pipe(
      take(1),
      map((resp: BaseResponse<GfasPayload>): GfasPayload => {
        const {data} = resp;
        if (!data) {
          this.loading = false;
          this._recallsVehicle.vin = this.vin;
          this.recallsPage.recallsVehicle = {...this._recallsVehicle};
          this.setActiveVehicle(this.vin);
          this.onRecallsResponseError(resp);
          if (!this.flag.invalidVinError) throw new Error(`Unable to retrieve recall inforamtion for vin: ${this.vin}`);
        }
        return data;
      })
    );
  }

  private processRecalls(data: GfasPayload) {
    this._recallsVehicle.vin = data?.vin || this.vin;
    this.recallsPage.recallsVehicle = {...this._recallsVehicle};
    this.recallResults = data?.recallResults ? data.recallResults : [];
    this.completeRecallResults = data?.completeRecallResults ? data.completeRecallResults : [];
    this.specialWarranties = data?.specialWarranties ? data.specialWarranties : [];
    this.flag.recallSearchCompleted = this.flag.isAnotherSearch = true;
    this.flag.nhtsaRecallExists = this.flag.recallWidgetSignupSuccess = false;
    this.recallsPage.odometerUpdateError = false;
    if (data?.gfas) {
      this.setNhtsaRecallState(data.gfas);
      this.discoverTakataRecalls(data.gfas);
    }
    if (data && data.make && data.year && data.model) {
      this.setSearchVehicle(data as unknown as RecallsVehicle);
    }
    this.trackSearch(data);
    this.reset();
    this.loading = false;
  }

  private getRecalls() {
    this.loading = true;
    this.scrollTop();
    this._search()
      .subscribe({
        next: (data) => this.processRecalls(data),
        error: (error: HttpErrorResponse) => this.onRecallsError(error)
      });
  }

  private getTakataEligibleRecallNumbers$(): Observable<string[]> {
    if (recallsConfigItems.recallCenter.useCmsRecalls) {
      const id = 'recall_center_config_list';
      return this.translate.get(id)
        .pipe(takeWhile(res => res !== id),
          map((takataRecallNumbers) => takataRecallNumbers.split(',').map((takataRecall: string) => {
              return takataRecall.trim();
            })
          ));
    }
    return of([]);
  }

  private discoverTakataRecalls(recalls: RecallPayload[]) {
    this.recallsPage.displayTakataWidget = false;
    this.stayUpToDateRecalls = [];
    this.getTakataEligibleRecallNumbers$().pipe(
      take(1),
      mergeMap((takataRecallNumbers: string[]) => {
        for (const recall of recalls) {
          recall.governmentAgencies.forEach(agency=>{
            if(agency.govtAgencyId === 'NHTSA'){
              if (takataRecallNumbers.includes(agency.govtAgencyNum)) {
                this.stayUpToDateRecalls.push(agency.govtAgencyNum);
              }
            }
          });

        }
        this.currentTakataRecalls = this.stayUpToDateRecalls.join(',');
        if(this.stayUpToDateRecalls.length > 0) {
          this.recallsPage.displayTakataWidget = true;
          this.trackingService.trackEvent('event310', 'recall.takata.load');
        }
        this.recallsPage.takataEligibleRecallsNumbers = this.currentTakataRecalls;
        return of(this.currentTakataRecalls);
      })
    ).subscribe();
  }

  private setNhtsaRecallState(recallResults: RecallPayload[]) {
    for (const value of Object.values(recallResults)) {
      if (!this.flag.nhtsaRecallExists) {
        this.flag.nhtsaRecallExists = value.type === 'ZPSR' || value.type === 'ZNCR' || value.type === 'ZPER';
        break;
      }
    }
    this.flag.nonNhtsaRecallExists = !this.flag.nhtsaRecallExists;
  }

  private setSearchVehicle({
                             vin = '',
                             year = '',
                             make = '',
                             model = '',
                             renderImageUrl = '',
                             odometer = '0',
                             odometerLastUpdated = -1
                           }: Partial<RecallsVehicle>) {
    if (this.activeVehicle && this.urlVin === this.activeVehicle?.vin) this._recallsVehicle = {...this.activeVehicle};
    else this._recallsVehicle = {year, make, model, renderImageUrl, vin, odometer, odometerLastUpdated};
    this.recallsPage.recallsVehicle = {...this._recallsVehicle};
  }

  private setActiveVehicle(vin: string, id?: string) {
    let currentGarageVehicle: Vehicle | undefined;
    const dispatchVehicle = (vehicle: Vehicle) => this.store.dispatch(ActiveVehicleActions.update({vehicle}));
    const pluckByVin = (vehicles: IterableIterator<Vehicle>) => {
      for (const vehicle of vehicles) {
        if (vin === vehicle.vin) {
          currentGarageVehicle = vehicle;
          break;
        }
      }
      return currentGarageVehicle;
    };
    currentGarageVehicle = this.userState === AUTH ? id ?
      this.garage.get(id) :
      vin ?
        pluckByVin(this.garage.values()) :
        undefined : undefined;
    if (!currentGarageVehicle) return console.warn(`Vehicle: ${vin ?? ''} ${id ?? ''} not found in garage`);
    dispatchVehicle(currentGarageVehicle);
  }

  private onRecallsError(error: HttpErrorResponse) {
    this.flag.recallErrorState = this.serverError = true;
    this.invalidVin = this.flag.recallSearchCompleted = false;
    this.errorMessage = error.message;
    this.setRecallErrorProperties();
  }

  private isYMMVehicle() {
    this.loading = this.flag.recallErrorState = this.flag.warrantyErrorState = this.flag.isAnotherSearch = this.flag.nhtsaRecallExists = this.flag.nonNhtsaRecallExists = this.flag.recallSearchCompleted = this.flag.warrantySearchCompleted = false;
    this.recallsPage.displayTakataWidget = false;
    this.recallsPage.warrantError = false;
    this.mmyFound = true;
  }

  private onRecallsResponseError(error: BaseResponse<GfasPayload>) {
    const messages = error?.messages?.[0] || undefined;
    this.flag.recallErrorState = true;
    this.serverError = this.flag.recallSearchCompleted = false;
    this.recallsPage.displayTakataWidget = false;
    if (messages) this.errorMessage = messages;
    if (messages === 'VEHICLE_INVALID_VIN') {
      this.flag.invalidVinError = this.flag.recallSearchCompleted = true;
    }
    this.setRecallErrorProperties();
  }

  private onWarrantiesResponseError(error: BaseResponse<GfasPayload>) {
    const messages = error?.messages?.[0] || undefined;
    this.flag.warrantyErrorState = true;
    this.recallsPage.warrantError = true;
    this.serverError = this.flag.warrantySearchCompleted = false;
    if (messages) this.errorMessage = messages;
    this.setWarrantyErrorProperties();
  }

  private setRecallErrorProperties() {
    this.recallResults = [];
    this.completeRecallResults = [];
  }

  private setWarrantyErrorProperties() {
    this.warrantyResults = {
      active: [],
      expired: []
    };
    this.specialWarranties = [];
  }

  private trackSearch(data:GfasPayload) {
    const resultsCount = this.recallResults.length + this.completeRecallResults.length;
    this.dataLayerV2Enabled$.subscribe((dataLayerV2Enabled) => {
      let searchRecalls = {};
      if (dataLayerV2Enabled === 'true') {
        searchRecalls = {
          search : {
            term: this._recallsVehicle.vin,
            resultCount: resultsCount,
            resultClicked: undefined,
            facetsUsed: [`${this._recallsVehicle.year} ${this._recallsVehicle.make} ${this._recallsVehicle.model}`],
            filtersUsed: []
          }
        };
      } else {
        searchRecalls = {
          recalls: {
            vin: this._recallsVehicle.vin,
            type: data?.recallResults ? [...new Set(Object.values(data.gfas).map(({type}) => type))].join(',') : '',
            ymm: `${this._recallsVehicle.year} ${this._recallsVehicle.make} ${this._recallsVehicle.model}`,
            count: resultsCount
          }
        };
      }
      AdobeDataLayerBaseCall.setGmUnifiedProperty(searchRecalls, window);
    });

    this.translate.get('recallCenter_search_button').pipe(
      takeWhile((translationRes: string) => translationRes !== 'recallCenter_search_button'))
      .subscribe((translation:string)=>{
        this.trackRecallsSearch(translation, data);
      });
  }

  private trackRecallsSearch(searchTranslation:string, response: GfasPayload){
    const onError = (response: BaseResponse<GfasPayload>|undefined) => {
      delete this.omni.prop46;
      delete this.omni.prop48;
      delete this.omni.prop51;
      response?.messages?.includes('VEHICLE_INVALID_VIN') ? this.trackingService.trackEvent('event20', 'recall.invalid.vin') : this.trackingService.trackEvent('event25', 'recall.retrieval.failed');
    };

    const onSuccess = (data: GfasPayload) => {
      this.omni.prop46 =  data?.recallResults?.length.toString();
      this.omni.prop45 = data?.gfas ? [Object.values(data.gfas).filter((data:RecallPayload) => data?.vinStatusInfo.vinStatus === 'incomplete' && data?.type !== 'ZSPP').map(({type}) => type)].join(',') : undefined;
      this.omni.prop51 = this.prop51 =  data?.gfas ? [Object.values(data.gfas).filter((data:RecallPayload) => data?.vinStatusInfo.vinStatus === 'incomplete' && data?.type !== 'ZSPP').map(({gfaTexts}) => gfaTexts[0].subject)].join(',') : undefined;
      this.prop53 = data?.gfas ? [Object.values(data.gfas).filter((data:RecallPayload) => data?.vinStatusInfo.vinStatus === 'incomplete' && data?.type !== 'ZSPP').map(({gfaNumber}) => gfaNumber)].join(',') : '';
      const trackLink =  `recalls:${searchTranslation}`;
      const currentPageName = this.trackingService.getPageName();
      const attributes = {} as any;
      attributes['prop10'] = `recalls:${currentPageName}:hyperlink:click:${searchTranslation}`;
      attributes['prop15'] = searchTranslation;
      attributes['evar33'] = `recalls:${currentPageName}:hyperlink:click:${searchTranslation}`;
      this.trackingService.setProps(attributes['prop10']);
      this.trackingService.setEvars(attributes['evar33']);
      this.trackingService.fireTracking(true, 'o', trackLink);
    };

    this.omni.prop44 = this.vin;
    !response ? onError(response) : onSuccess(response);
    this.resetRecallSearchTracking();
  }

  private resetRecallSearchTracking(){
    delete this.omni.prop46;
    delete this.omni.prop45;
    delete this.omni.prop51;
    delete this.omni.prop44;
  }

  private reset() {
    this.flag.invalidVinError = false;
    this.serverError = false;
    this.flag.recallErrorState = false;
    this.vin = '';
  }

  private scrollTop() {
    window.scrollTo(0, 0);
  }

  private decryptVin(vin_enc: string) {
    this.decryptVinService.decryptVin(vin_enc)
      .pipe(take(1))
      .subscribe({
          next: (decryptedVin) => this.setVinInUrl(decryptedVin),
          error: (e) => {
            this._removeUrlVinParam();
            this.setVinInUrl(this._recallsVehicle.vin);
          },
          complete: () => this.decryptingVin = false
        }
      );
  }

  private isVinValid(vin: string) {
    return (!!vin && this.vinAttributes?.pattern?.test(vin) && vin.length === this.vinAttributes.maxlength);
  }

  private _removeUrlVinParam() {
    const params = {queryParams: {vin_enc: null}, queryParamsHandling: 'merge'} as NavigationExtras;
    this.router.navigate([], params);
  }
}
